class OpAlgoliaHeaderSearch extends HTMLElement {
  constructor() {
    super();
    this.STORAGE_KEY = "op-search-history";
    this.logoUrl = this.dataset.logoUrl;
    this.locales = this.querySelector(".op-search-locales") ? JSON.parse(this.querySelector(".op-search-locales").textContent) : {};
    this.recentSearches = JSON.parse(localStorage.getItem(this.STORAGE_KEY)) ? JSON.parse(localStorage.getItem(this.STORAGE_KEY)).slice(0, 3) : [];
    this.lastResultCount = 0;
    this.labelSearch = this.dataset.searchPlaceholder ? this.dataset.searchPlaceholder : '';
    this.searchTimer = null;
    this.delayTime = 2000;
    this.currentResultTypes = { hasProducts: false, hasContent: false };

  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.initAlgoliaHooks();
    this.setRecentSearches();
    this.moveAlgoliaPanel();
    this.observeDOMChanges();
    this.observeSearchForm();
    this.addSearchInputListener();
    this.observeAutocompleteClicks();
    this.setupDebouncedInputListener();

    document.dispatchEvent(new CustomEvent('la:dn:collection:updated'));
    this.querySelector(".modal__close-button")?.addEventListener("click", (e) => {
      this.closest(".show")?.classList.remove("show");
      if(document.querySelector('.aa-Input')) {
        document.querySelector('.aa-Input').value = ''
        OpAlgoliaHeaderSearch.updateAlgoliatState()
      }
    });
  }

  addListenersToNewElements() {
    const suggestionItems = document.querySelectorAll('.aa-Item, .aa-ItemLink, .aa-Form');
    suggestionItems.forEach(item => {
      if (!item.dataset.listenerAdded) {
        item.dataset.listenerAdded = 'true';
        item.addEventListener('input', this.handleAutocompleteClick.bind(this));
      }
    });

    const productCards = document.querySelectorAll('#opAlgoliaResultsContainer .aa-Item');
    productCards.forEach(card => {
      if (!card.dataset.listenerAdded) {
        card.dataset.listenerAdded = 'true';
        card.addEventListener('click', this.handleProductClick.bind(this));
      }
    });
  }

  static validateDateAnalytics(str) {
    if (!str) return '';

    return str
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, ' ')
      .trim()
      .split(' ')
      .map((word, index) => {
        if (index === 0) {
          return word.toLowerCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join('');
  }

  static normalizeAlgoliaValue(value) {
    return value !== undefined && value !== null
      ? value.toString().trim().toLowerCase()
      : "";
  }

  static normalizeAlgoliaId(value) {
    const str = OpAlgoliaHeaderSearch.normalizeAlgoliaValue(value);
    const digits = str.replace(/\D/g, "");
    return digits || str;
  }

  static normalizeAlgoliaMarketToken(value) {
    return OpAlgoliaHeaderSearch.normalizeAlgoliaValue(value).replace(/[-\s]+/g, "_");
  }

  static normalizeAlgoliaPortugalSuffix(suffix) {
    return suffix.startsWith("portugal_")
      ? suffix
          .replace(/_(en)(?:_pt)?$/, "_$1")
          .replace(/_(pt)(?:_pt)?$/, "_$1")
      : suffix;
  }

  static normalizeAlgoliaIndexSuffix(suffix) {
    const normalizedSuffix = OpAlgoliaHeaderSearch.normalizeAlgoliaPortugalSuffix(
      OpAlgoliaHeaderSearch.normalizeAlgoliaMarketToken(suffix)
        .replace(/[^a-z0-9_]+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "")
    );

    return normalizedSuffix ? `_${normalizedSuffix}` : "";
  }

  static normalizeAlgoliaIndexName(indexName) {
    if (!indexName) return indexName;

    return indexName
      .toString()
      .replace(/(_portugal)_(en)(?:[-_]pt)?(?=(_query_suggestions)?$)/i, "$1_$2")
      .replace(/(_portugal)_(pt)(?:[-_]pt)?(?=(_query_suggestions)?$)/i, "$1_$2");
  }

  static getAlgoliaLocaleCandidates(shopifyData) {
    const locale = OpAlgoliaHeaderSearch.normalizeAlgoliaMarketToken(shopifyData?.market?.language?.shop_locale?.locale);
    const shopifyLocale = OpAlgoliaHeaderSearch.normalizeAlgoliaMarketToken(window.Shopify?.locale);
    const country = OpAlgoliaHeaderSearch.normalizeAlgoliaMarketToken(shopifyData?.country || window.Shopify?.country);
    const candidates = locale ? [locale] : [shopifyLocale];

    candidates.forEach((candidate) => {
      if (candidate && country && !candidate.endsWith(`_${country}`)) {
        candidates.push(`${candidate}_${country}`);
      }
    });

    return candidates.filter(Boolean);
  }

  static algoliaMarketMatchesLocale(market, localeCandidates = []) {
    const marketKey = OpAlgoliaHeaderSearch.normalizeAlgoliaMarketToken(market?.key);
    const marketSuffix = OpAlgoliaHeaderSearch.normalizeAlgoliaMarketToken(market?.suffix);
    const marketIndex = OpAlgoliaHeaderSearch.normalizeAlgoliaMarketToken(market?.index_name);

    return localeCandidates.some((locale) =>
      marketKey.includes(locale) ||
      marketSuffix.includes(locale) ||
      marketIndex.includes(locale)
    );
  }

  static resolveAlgoliaMarket(algoliaConfig, shopifyData) {
    const marketsMapping = algoliaConfig?.markets_mapping;
    if (!Array.isArray(marketsMapping) || marketsMapping.length === 0) return null;

    const marketId = OpAlgoliaHeaderSearch.normalizeAlgoliaId(shopifyData?.market?.id);
    const country = OpAlgoliaHeaderSearch.normalizeAlgoliaValue(shopifyData?.country || window.Shopify?.country);
    const localeCandidates = OpAlgoliaHeaderSearch.getAlgoliaLocaleCandidates(shopifyData);

    return (
      marketsMapping.find(
        (market) =>
          OpAlgoliaHeaderSearch.normalizeAlgoliaId(market.id) === marketId &&
          OpAlgoliaHeaderSearch.algoliaMarketMatchesLocale(market, localeCandidates)
      ) ||
      marketsMapping.find((market) => OpAlgoliaHeaderSearch.normalizeAlgoliaId(market.id) === marketId) ||
      marketsMapping.find(
        (market) =>
          OpAlgoliaHeaderSearch.algoliaMarketMatchesLocale(market, localeCandidates) ||
          (country && OpAlgoliaHeaderSearch.normalizeAlgoliaValue(market.key).includes(country))
      ) ||
      marketsMapping[0]
    );
  }

  static resolveAlgoliaProductsIndex(algoliaConfig, shopifyData) {
    if (!algoliaConfig) return null;

    const indexPrefix = algoliaConfig.index_prefix
      ? algoliaConfig.index_prefix
      : algoliaConfig.index_name && algoliaConfig.index_name.includes("products")
        ? algoliaConfig.index_name.split("products")[0]
        : "";
    const currentMarket = OpAlgoliaHeaderSearch.resolveAlgoliaMarket(algoliaConfig, shopifyData);
    const resolvedSuffix = OpAlgoliaHeaderSearch.normalizeAlgoliaIndexSuffix(currentMarket?.suffix);

    if (!indexPrefix) {
      return OpAlgoliaHeaderSearch.normalizeAlgoliaIndexName(currentMarket?.index_name || algoliaConfig.index_name || null);
    }

    return OpAlgoliaHeaderSearch.normalizeAlgoliaIndexName(`${indexPrefix}products${resolvedSuffix}`);
  }

  static resolveAlgoliaQuerySuggestionsIndex(algoliaConfig, productsIndex, querySuggIndex) {
    const configuredIndex = querySuggIndex || algoliaConfig?.query_suggestions_index_name;
    const normalizedConfiguredIndex = OpAlgoliaHeaderSearch.normalizeAlgoliaIndexName(configuredIndex);

    if (normalizedConfiguredIndex) {
      return normalizedConfiguredIndex;
    }

    return productsIndex && productsIndex.includes("_portugal")
      ? `${productsIndex}_query_suggestions`
      : null;
  }

  static applyResolvedAlgoliaIndexes(algoliaConfig, shopifyData, querySuggIndex) {
    if (!algoliaConfig) return null;

    const productsIndex = OpAlgoliaHeaderSearch.resolveAlgoliaProductsIndex(algoliaConfig, shopifyData);
    if (productsIndex) {
      algoliaConfig.index_name = productsIndex;
    }

    const querySuggestionsIndex = OpAlgoliaHeaderSearch.resolveAlgoliaQuerySuggestionsIndex(
      algoliaConfig,
      productsIndex,
      querySuggIndex
    );

    if (querySuggestionsIndex) {
      algoliaConfig.query_suggestions_index_name = querySuggestionsIndex;
    }

    return productsIndex;
  }

  static wrapAlgoliaSearchClient(searchClient) {
    if (!searchClient || searchClient.__opPortugalIndexNormalizer || typeof searchClient.search !== "function") {
      return;
    }

    const originalSearch = searchClient.search.bind(searchClient);
    searchClient.search = (requests, ...rest) => {
      const normalizedRequests = Array.isArray(requests)
        ? requests.map((request) => ({
            ...request,
            indexName: OpAlgoliaHeaderSearch.normalizeAlgoliaIndexName(request.indexName),
          }))
        : requests;

      return originalSearch(normalizedRequests, ...rest);
    };

    searchClient.__opPortugalIndexNormalizer = true;
  }

  observeAutocompleteClicks() {
    const observer = new MutationObserver(() => {
      this.addListenersToNewElements();
    });
    observer.observe(document.body, {childList: true, subtree: true});
  }

  setupDebouncedInputListener() {
    const DEBOUNCE_DELAY = 300;

    const debouncedAddListeners = GeneralUtils.debounce(() => {
      this.addListenersToNewElements();
    }, DEBOUNCE_DELAY);

    const inputObserver = new MutationObserver(() => {
      const searchInput = document.querySelector('.aa-Input');
      if (searchInput && !searchInput.dataset.inputListenerAdded) {
        searchInput.dataset.inputListenerAdded = 'true';

        searchInput.addEventListener('input', debouncedAddListeners);

        inputObserver.disconnect();
      }
    });

    inputObserver.observe(document.body, {childList: true, subtree: true});
  }

  static updateAlgoliatState(defaultState = true) {
    const algoliaResultsContainer = document.getElementById('opAlgoliaResultsContainer')
    const opAlgoliaNoDataContainerDesktop = document.getElementById('opAlgoliaNoDataContainerDesktop')
    const opSearchPopularItemsContainer = document.querySelector('.popular-items-container')
    const opRecentSearchesContainer = document.getElementById('op-recent-searches')
    const opSearchPopularItemsContainerMobile = document.querySelector('.popular-items-container-mobile')
  
    defaultState ? opAlgoliaNoDataContainerDesktop.classList.add('tw-hidden') : opAlgoliaNoDataContainerDesktop.classList.remove('tw-hidden')
    defaultState ? algoliaResultsContainer.querySelector('.aa-PanelLayout')?.classList.add('tw-hidden') : algoliaResultsContainer.querySelector('.aa-PanelLayout')?.classList.remove('tw-hidden')
    defaultState ? opSearchPopularItemsContainer.style.display = 'block' : opSearchPopularItemsContainer.style.display = 'none'
    defaultState ? opRecentSearchesContainer.style.display = 'block' : opRecentSearchesContainer.style.display = 'none'
    defaultState ? opSearchPopularItemsContainerMobile?.classList.remove('tw-overflow-hidden','tw-h-0','tw-py-0') : opSearchPopularItemsContainerMobile?.classList.add('tw-overflow-hidden','tw-h-0','tw-py-0')
  }


  initAlgoliaHooks() {
    const querySuggIndex = this.dataset.querySuggIndex?.trim();

    document.addEventListener("algolia.hooks.initialize", function () {
      OpAlgoliaHeaderSearch.applyResolvedAlgoliaIndexes(
        window.algoliaShopify?.config,
        window.algoliaShopify?.shopify,
        querySuggIndex
      );
      OpAlgoliaHeaderSearch.wrapAlgoliaSearchClient(window.algoliaShopify?.searchClient);

      // Add the custom plugin to the Autocomplete.
      algoliaShopify.hooks.registerHook(
        "beforeAutocompleteOptions",
        function (options) {
          const DEBOUNCE_DELAY = 300;
          options.placeholder = this.labelSearch;
          const { searchClient } = window.algoliaShopify;
          const { getAlgoliaResults } = window.algoliaShopify.externals;
          const algoliaConfig = window.algoliaShopify.config;
          const productsIndex = OpAlgoliaHeaderSearch.applyResolvedAlgoliaIndexes(
            algoliaConfig,
            window.algoliaShopify?.shopify,
            querySuggIndex
          );
          OpAlgoliaHeaderSearch.wrapAlgoliaSearchClient(searchClient);

          let currentQuery = '';
          let positionCounter = 0;

          const upcCollapsedPlugin = {
            name: "custom-upc-collapsed-plugin",
            getSources({ query, setContext }) {
              return [
                {
                  sourceId: "upcCollapsed",
                  getItems() {
                    if (currentQuery !== query) {
                      currentQuery = query;
                      positionCounter = 0;
                    }
                    
                    return getAlgoliaResults({
                      searchClient,
                      queries: [
                        {
                          indexName: productsIndex || algoliaConfig.index_name,
                          query,
                          params: {
                            ruleContexts: "upc_collapsed",
                            hitsPerPage: 9
                          },
                        },
                      ],
                      transformResponse({ results, hits }) {
                        setContext({
                          nbHits: results[0].nbHits
                        });
                        return hits;
                      },
                    });
                  },
                  templates: {
                    header({ html }) {
                      positionCounter = 0;
                      return html` <div class="aa-SourceHeader">
                        <span class="aa-SourceHeaderTitle">Sale Items</span>
                        <div class="aa-SourceHeaderLine" />
                      </div>`;
                    },
                    item({ item, html }) {
                      positionCounter++;
                      const position = positionCounter;
                      const titleCamelCase = OpAlgoliaHeaderSearch.validateDateAnalytics(item.title);

                      return html`<op-dynamic-product-card data-product-template="op-product-card" data-product-handle="${item.handle}" data-element-id="SearchPanel_Results_${titleCamelCase}_${position}" data-description="Search_Results">
                      <div class="tw-bg-op-secondary tw-w-full tw-h-0 tw-pb-[100%]"><br/></div>
                      </op-dynamic-product-card>`;
                    },
                  },
                },
              ];
            },
          };
          options.plugins.push(upcCollapsedPlugin);

          options.onReset = function(state,e, setters) {
            OpAlgoliaHeaderSearch.updateAlgoliatState() 
          };
          options.onStateChange = function(autocompleteState) { 
            if(autocompleteState.state.isOpen == true && autocompleteState.state.query == '' ) {
              // Remove mobile modal if no results
              if(document.querySelector('.aa-DetachedOverlay')) {
                document.querySelector('.aa-DetachedOverlay').remove();
              }
            }
            if(autocompleteState.state.query == '' || autocompleteState.state.query.length < 2) {
              OpAlgoliaHeaderSearch.updateAlgoliatState() 
            } else {
              OpAlgoliaHeaderSearch.updateAlgoliatState(false) 
            }
          };
          const wrapGetSourcesWithDebounce = (plugins = [], delay) => {
            plugins
              .filter(({ getSources }) => typeof getSources === 'function')
              .forEach((plugin) => {
                const original = plugin.getSources;
                plugin.getSources = GeneralUtils.debounce(original, delay);
               });
          };
          
          wrapGetSourcesWithDebounce(options.plugins, DEBOUNCE_DELAY);
          return options;
        }.bind(this)
      );
    }.bind(this));

    // No Data Hook
    document.addEventListener("algolia.hooks.initialize", () => {
      algoliaShopify.hooks.registerHook(
        "beforeAutocompleteNoResultsTemplate",
        (_defaultTemplate, templateOptions) => {
          const { html, state } = templateOptions;
          document.querySelector('.popular-items-container').style.display = 'block'
          document.getElementById('op-recent-searches').style.display = 'block'

          return html`
            <div class="tw-font-heading opAlgoliaNoDataContainerMobile md:tw-hidden tw-text-h5 tw-m-op-spacings-base op-tablet-sm:tw-m-0 op-tablet-sm:tw-mb-op-spacings-xl">
              ${algolia.translations.noResultFound}
            </div>
          `;
        }
      );
    });

    // Main Template Hook
    document.addEventListener("algolia.hooks.initialize", () => {
      algoliaShopify.hooks.registerHook(
        "beforeAutocompleteMainTemplate",
        (_defaultTemplate, templateOptions, elements, displaySuggestions) => {
          const { html, state } = templateOptions;
          const searchQuery = state.query;

            this.lastResultCount = state.context.nbProducts;
            const totalItems = state.context.nbHits;

            this.currentResultTypes.hasProducts = !!elements.upcCollapsed;
            this.currentResultTypes.hasContent = !!elements.articles;

            document.getElementById('opAlgoliaNoDataContainerDesktop').classList.add('tw-hidden')
            if(state.query.length >= 2) {
              // Show
              return html`
              <div class="aa-PanelLayout tw-op-scrollbar !tw-pr-op-spacings-md tw-w-full [&_.aa-SourceHeader]:tw-hidden tw-flex">
                <div class="algolia-search-suggestions tw-hidden op-tablet-md:tw-block has-[.aa-Source:empty]:tw-hidden tw-min-w-[368px] [&_.aa-ItemActions]:tw-hidden [&_.aa-ItemIcon]:tw-hidden [&_.aa-ItemContentTitle]:tw-typo-op-base [&_.aa-ItemContentTitle]:tw-text-op-tertiary [&_.aa-Item[aria-selected=true]]:!tw-bg-transparent [&_.aa-Item]:tw-p-0 [&_.aa-Item]:tw-min-h-0 [&_.aa-List]:tw-space-y-op-spacings-md tw-space-y-op-spacings-base tw-h-fit tw-sticky tw-top-0">
                  <h3 class="tw-typo-op-h3">${elements.querySuggestionsPlugin ? this.locales.items : ""}</h3>
                  <div class="query-suggestions-wrapper">${elements.querySuggestionsPlugin}</div>
                </div>
                <div class="tw-w-full">
                  <div class="tw-hidden op-tablet-md:tw-flex op-tablet-md:tw-justify-end op-tablet-md:tw-items-center op-tablet-md:tw-gap-op-spacings-md op-tablet-md:tw-mb-op-spacings-base">
                      <a data-element-id="SearchPanel_Results_ViewAll" data-description="ViewAll_${searchQuery}" href="${Shopify.routes.root}search?q=${encodeURIComponent(searchQuery)}" class="tw-underline tw-typo-op-cta-medium">
                      ${this.locales.viewAllResults} "${searchQuery}" [${totalItems}]
                    </a>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.0808 0.544556L15.911 7.49998L9.0808 14.4554L8.15325 13.5446L13.4504 8.1503H0V6.8503H13.451L8.15325 1.45541L9.0808 0.544556Z" fill="#202125"/></svg>
                  </div>
                  <div class="[&_.aa-Item]:tw-items-start [&_.aa-List]:tw-grid [&_.aa-List]:tw-grid-cols-2 [&_.aa-List]:md:tw-grid-cols-1 [&_.aa-Item[aria-selected=true]]:!tw-bg-transparent">
                    <h3 class="tw-typo-op-h3 tw-pb-op-spacings-lg tw-pt-op-spacings-base tw-px-op-spacings-md op-tablet-sm:tw-px-0">${elements.upcCollapsed ? this.locales.items : ""}</h3>
                    ${elements.upcCollapsed}
                  </div>
                  <div class="tw-flex tw-justify-start tw-items-center tw-gap-op-spacings-md op-tablet-md:tw-hidden">
                    <a href="${Shopify.routes.root}search?q=${encodeURIComponent(searchQuery)}" class="tw-underline tw-typo-op-cta-medium tw-p-op-spacings-md">
                      ${this.locales.viewAllResults} "${searchQuery}" [${totalItems}]
                    </a>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.0808 0.544556L15.911 7.49998L9.0808 14.4554L8.15325 13.5446L13.4504 8.1503H0V6.8503H13.451L8.15325 1.45541L9.0808 0.544556Z" fill="#202125"/></svg>
                  </div>
                  <div class="[&_.aa-List]:tw-grid tw-mt-op-spacings-lg [&_.aa-List]:tw-grid-cols-1 [&_.aa-List]:md:tw-grid-cols-2 [&_.aa-List]:lg:tw-grid-cols-3 [&_.aa-List]:tw-gap-6">
                    <h3 class="tw-typo-op-h3 tw-pb-op-spacings-lg tw-pt-op-spacings-base tw-op-spacings-px-md op-tablet-sm:tw-px-0">${elements.articles ? this.locales.featuredContent : ""}</h3>
                    ${elements.articles}
                  </div>
                </div>
              </div>
              `;
            }
        }
      );
    });

    // Products section (rendered when ONLY products are available)
    // document.addEventListener("algolia.hooks.initialize", function () {
    //   algoliaShopify.hooks.registerHook(
    //     "beforeAutocompleteMainProductsTemplate",
    //     function (_defaultTemplate, templateOptions, elements) {
    //       console.log("Products section template hook called");
    //       const { html, state } = templateOptions;
    //       const searchQuery = state.query;
    //       const totalItems = state.collections
    //         .map((collection) => collection.items.length)
    //         .reduce((acc, curr) => acc + curr, 0);
    //         const totalItemsa = state.context.nbProducts;
    //       console.log("total Items", totalItemsa);
    //       return html`
    //         <div class="aa-PanelLayout tw-op-scrollbar !tw-pr-op-spacings-md tw-w-full">
    //           <div
    //             class="tw-hidden op-tablet-md:tw-flex op-tablet-md:tw-justify-end op-tablet-md:tw-items-center op-tablet-md:tw-gap-op-spacings-md op-tablet-md:tw-mb-op-spacings-base"
    //           >
    //             <a
    //               href="${Shopify.routes.root}search?q=${encodeURIComponent(searchQuery)}"
    //               class="tw-underline tw-typo-op-cta-medium"
    //             >
    //               View all search results for "${searchQuery}" (${totalItemsa})
    //             </a>
    //             <svg
    //               xmlns="http://www.w3.org/2000/svg"
    //               width="16"
    //               height="16"
    //               viewBox="0 0 16 16"
    //               fill="none"
    //             >
    //               <path
    //                 fill-rule="evenodd"
    //                 clip-rule="evenodd"
    //                 d="M9.0808 0.544556L15.911 7.49998L9.0808 14.4554L8.15325 13.5446L13.4504 8.1503H0V6.8503H13.451L8.15325 1.45541L9.0808 0.544556Z"
    //                 fill="#202125"
    //               />
    //             </svg>
    //           </div>
    //           <div class="aa-PanelSection">${elements.products}</div>
    //           <div
    //             class="tw-flex tw-justify-start tw-items-center tw-gap-op-spacings-md op-tablet-md:tw-hidden"
    //           >
    //             <a
    //               href="${Shopify.routes.root}search?q=${encodeURIComponent(searchQuery)}"
    //               class="tw-underline tw-typo-op-cta-medium"
    //             >
    //               View all search results for "${searchQuery}" (${totalItems})
    //             </a>
    //             <svg
    //               xmlns="http://www.w3.org/2000/svg"
    //               width="16"
    //               height="16"
    //               viewBox="0 0 16 16"
    //               fill="none"
    //             >
    //               <path
    //                 fill-rule="evenodd"
    //                 clip-rule="evenodd"
    //                 d="M9.0808 0.544556L15.911 7.49998L9.0808 14.4554L8.15325 13.5446L13.4504 8.1503H0V6.8503H13.451L8.15325 1.45541L9.0808 0.544556Z"
    //                 fill="#202125"
    //               />
    //             </svg>
    //           </div>
    //         </div>
    //       `;
    //     }
    //   );
    // });

    // Product Template Hook
    
    // document.addEventListener("algolia.hooks.initialize", () => {
    //   algoliaShopify.hooks.registerHook(
    //     "beforeAutocompleteProductTemplate",
    //     function (
    //       _defaultTemplate,
    //       templateOptions,
    //       distinct,
    //       itemLink,
    //       trackSearchAttribution
    //     ) {
    //       const { html, item, components } = templateOptions;
    //       // const handleItem = components.Highlight({ hit: item, attribute: "handle" });
    //       // const handleItemChild = handleItem.props.children[0];
    //       // const handle = typeof handleItemChild === "string" ? handleItemChild : item.handle;
    //       return html`<op-dynamic-product-card class="tw-hidden data-[loaded]:tw-block" data-product-template="op-product-card" data-product-handle="${item.handle}"> </op-dynamic-product-card>`;
    //     }
    //   );
    // });

    // Collections Template Hook
    document.addEventListener("algolia.hooks.initialize", function () {
      algoliaShopify.hooks.registerHook(
        "beforeAutocompleteCollectionsTemplate",
        function (_defaultTemplate, templateOptions, itemLink) {
          const { html, item, components } = templateOptions;
          return html`
          <div class="tw-block">
            <a href="${itemLink}">
              <div
                class="tw-p-op-spacings-base tw-flex tw-flex-col-reverse tw-aspect-square"
                style="background-image: url('${item.image || algoliaShopify.helpers.compactImage(
            item
          )}'); background-size: cover; background-position: center;"
              >
                <span class="tw-text-white tw-typo-op-h2 tw-text-h2">
                  ${components.Highlight({ hit: item, attribute: "title" })}
                </span>
              </div>
            </a>
          </div>
          `;
        }
      );
    });

    // Articles Template Hook
    document.addEventListener("algolia.hooks.initialize", function () {
      algoliaShopify.hooks.registerHook(
        "beforeAutocompleteArticlesTemplate",
        function (_defaultTemplate, templateOptions, itemLink) {
          const { item, html, components } = templateOptions;
          return html`
            <a href="${itemLink}" class="tw-w-full tw-h-full">
              <div
                class="tw-p-op-spacings-base tw-flex tw-flex-col-reverse tw-aspect-square"
                style="background-image: url('${item.image || algoliaShopify.helpers.compactImage(
            item
          )}'); background-size: cover; background-position: center;"
              >
                <span class="tw-text-white tw-typo-op-h2 tw-text-h2">
                  ${item.title}
                </span>
              </div>
            </a>
          `;
        }
      );
    });

    // Pages Template Hook
    document.addEventListener("algolia.hooks.initialize", function () {
      algoliaShopify.hooks.registerHook(
        "beforeAutocompletePagesTemplate",
        function (_defaultTemplate, templateOptions, itemLink) {
          const { html, item, components } = templateOptions;
          return html`
            <a href="${itemLink}">
              <div
                class="tw-p-op-spacings-base tw-flex tw-flex-col-reverse tw-aspect-square"
                style="background-image: url('${item.image_high_res || item.image}'); background-size: contain; background-position: center;"
              >
                <span class="tw-text-white tw-typo-op-h2 tw-text-h2">
                  ${components.Highlight({ hit: item, attribute: "title" })}
                </span>
              </div>
            </a>
          `;
        }
      );
    });

    document.addEventListener("algolia.hooks.initialize", () => {
      const observer = new MutationObserver(() => {
        const mobileSearchInput = document.querySelector(".search__input");
        if (mobileSearchInput) {
          const customDiv = document.createElement("div");
          customDiv.className = "custom-detached-search";
          customDiv.innerHTML = 
          `<span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.8047 14.862L11.8277 10.885C12.768 9.73567 13.3333 8.26733 13.3333 6.66667C13.3333 2.98467 10.3487 0 6.66667 0C2.98467 0 0 2.98467 0 6.66667C0 10.3487 2.98467 13.3333 6.66667 13.3333C8.26733 13.3333 9.73567 12.768 10.885 11.8277L14.862 15.8047L15.8047 14.862ZM6.66667 12C3.726 12 1.33333 9.60733 1.33333 6.66667C1.33333 3.726 3.726 1.33333 6.66667 1.33333C9.60733 1.33333 12 3.726 12 6.66667C12 9.60733 9.60733 12 6.66667 12Z" fill="#202125"/>
              </svg>
            </span>`;
          mobileSearchInput.append(customDiv);
          observer.disconnect(); 
        }
      });
    
      observer.observe(document.body, { childList: true, subtree: true });
    });

    document.addEventListener("algolia.hooks.initialize", () => {
      const observer = new MutationObserver(() => {
        const detachedContainer = document.querySelector(".aa-DetachedContainer");
        if (detachedContainer && !document.querySelector(".custom-detached-content") && window.innerWidth < 601) {
          const detachedContainerParent = document.querySelector(".aa-DetachedContainer").parentElement;
          
          const customDiv = document.createElement("div");
          customDiv.className = "custom-detached-content";
          customDiv.innerHTML = 
          `<div class="tw-w-full tw-bg-white op-tablet-md:tw-bg-neutral-lightest tw-px-op-spacings-base op-tablet-md:tw-px-op-spacings-xl tw-py-[3.6rem] tw-flex tw-justify-center op-tablet-md:tw-justify-between ">
            <div>
              <img src="${this.logoUrl}" alt="Website logo">
            </div>
            <div>
              <button type="button" class="search-modal__close-button modal__close-button link link--text focus-inset !tw-items-start" aria-label="Close">
                <span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17"><path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"></path></svg>
                </span>
              </button>
            </div>
          </div>`;
          customDiv.classList.add("tw-bg-white", "tw-z-[9999]", "tw-fixed", "tw-top-0", "tw-w-full");
          detachedContainer.classList.add("!tw-top-[104px]", "[&_.aa-Input]:tw-text-base", "[&_.aa-Input]:tw-font-body", "[&_.aa-DetachedCancelButton]:tw-hidden", "[&_form]:tw-w-[93vw]","[&_.aa-Form:focus-within]:tw-shadow-none", "[&_.aa-Form:focus-within]:tw-outline-0", "!tw-shadow-none");
          detachedContainerParent.insertBefore(customDiv,detachedContainer);
          observer.disconnect(); 
        }
      });
    
      observer.observe(document.body, { childList: true, subtree: true });

      // Append popular items and searches on mobile noResultsContainer
      const newObserver = new MutationObserver(() => {
        const panelContainer = document.querySelector(".aa-DetachedContainer .aa-Panel");
        const popularItemsContainer = document.querySelector(".popular-items-container")
        if(panelContainer && popularItemsContainer && window.innerWidth < 768) {
          const popularItemsWrapper = document.createElement("div");
          const popularItemsDiv = document.createElement("div");
          popularItemsWrapper.className = "popular-items-container-mobile";
          popularItemsWrapper.classList.add("tw-order-1");
          popularItemsWrapper.append(popularItemsDiv);
          popularItemsDiv.classList.add("tw-w-full","tw-px-[1.6rem]","tw-py-[2.4rem]","md:tw-px-[2.4rem]", "tw-space-y-lg","tw-op-scrollbar","has-[op-algolia-trending:empty]:tw-hidden");
          popularItemsDiv.innerHTML = document.querySelector('.popular-items-container').innerHTML
          panelContainer.append(popularItemsWrapper);
          const popularSearchesDiv = document.createElement("div");
          popularSearchesDiv.className = "popular-searches-mobile";
          popularSearchesDiv.classList.add("tw-order-2","tw-font-body","tw-mb-xl","tw-p-base","md:tw-p-lg");
          popularSearchesDiv.innerHTML = document.getElementById('opPopularSearchesMobile').innerHTML
          panelContainer.append(popularSearchesDiv);
          newObserver.disconnect(); 
        }
      });
    
      newObserver.observe(document.body, { childList: true, subtree: true });
    });
  }

  createRecentSearchItem(search) {
    const li = document.createElement("li");
    li.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 0C3.58167 0 0 3.58167 0 8C0 12.4183 3.58167 16 8 16C12.4183 16 16 12.4183 16 8C16 3.58167 12.4183 0 8 0ZM8 14.6087C4.356 14.6087 1.39133 11.644 1.39133 8C1.39133 4.356 4.356 1.39133 8 1.39133C11.644 1.39133 14.6087 4.356 14.6087 8C14.6087 11.644 11.644 14.6087 8 14.6087Z" fill="#69697A"/>
          <path d="M8.66658 3.3335H7.33325V8.2765L10.8619 11.8048L11.8046 10.8622L8.66658 7.72383V3.3335Z" fill="#69697A"/>
        </svg>
        <a data-element-id="SearchPanel_Results_link" data-description="Recently_Search" href="${Shopify.routes.root}search?q=${search}">${search}</a>
      `;
    return li;
  }

  setRecentSearches() {
    const recentSearchesContainer = document.querySelector("#opRecentSearches");
    const recentSearchesUl = document.querySelector("#opRecentSearches ul");

    if (!recentSearchesUl || !recentSearchesContainer) return;

    recentSearchesUl.innerHTML = "";

    if (this.recentSearches.length === 0) {
      recentSearchesContainer.classList.add("tw-hidden");
      return;
    }

    this.recentSearches.forEach((search) =>
      recentSearchesUl.appendChild(this.createRecentSearchItem(search))
    );
  }

  moveAlgoliaPanel() {
    const panel = document.querySelector(
      ".aa-PanelLayout"
    );
    let target;

    if(window.innerWidth < 601) { 
      target =  document.getElementById("opAlgoliaMobileResultsContainer")
    } else {
      target = document.getElementById("opAlgoliaResultsContainer");
    }
  
    if (panel && target && panel.parentNode !== target) {
      target.appendChild(panel);
    }
  }

  observeDOMChanges() {
    const observer = new MutationObserver(() => this.moveAlgoliaPanel());
    observer.observe(document.body, { childList: true, subtree: true });
  }

  handleSearchFormSubmit(event) {
  event.preventDefault();
  const searchInput = document.querySelector(".aa-Form .aa-Input");
  if (searchInput) {
    this.setRecentSearches();
    this.publishSearchSubmit(searchInput.value);
    window.location.href = `/${Shopify.locale}-${Shopify.country}/search?q=${encodeURIComponent(searchInput.value)}`;
  }
}


  handleProductClick(event) {
    const clickedElement = event.currentTarget;
    const searchQuery = document.querySelector(".aa-Form .aa-Input")?.value || '';

    const productTitle = clickedElement.querySelector('.tile-title, .aa-ItemContentTitle')?.textContent?.trim() || '';

    this.publishSearchSubmit(searchQuery, 'product_click', productTitle);
  }

  publishSearchSubmit(searchTerm) {

    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
      
    this.searchTimer = setTimeout(() => {

      let facetValues = "";
      if (this.currentResultTypes.hasProducts && this.currentResultTypes.hasContent) {
        facetValues = "category=product|category=content";
      } else if (this.currentResultTypes.hasProducts) {
        facetValues = "category=product";
      } else if (this.currentResultTypes.hasContent) {
        facetValues = "category=content";
      }
      const tealiumPubObj = {
        "Events_SearchRun": "1",
        "Search_Keyword": searchTerm,
        "Search_ResultItemsQnt": `${this.lastResultCount}`,
        "Search_FacetValues_String": facetValues,
        "Search_Type": "text",
        "Search_KeyActual": searchTerm,
        "Search_View": "SERB"
      };

      const resultElements = document.querySelectorAll('#opAlgoliaResultsContainer .aa-Item');

      if (resultElements.length > 0) {
        tealiumPubObj["Search_ResultItemsQnt"] = resultElements.length.toString();
        }

        publish(PUB_SUB_EVENTS.searchSubmit, tealiumPubObj);
        
        this.searchTimer = null;
      }, this.delayTime);
  }

  handleAutocompleteClick(event) {
    const clickedElement = event.currentTarget;
    const searchQuery = document.querySelector(".aa-Form .aa-Input")?.value || '';

    let clickedSuggestion = '';
    const suggestionText = clickedElement.querySelector('.aa-ItemContentTitle .aa-ItemContent .aa-Input .aa-InputWrapperPrefix');
    if (suggestionText) {
      clickedSuggestion = suggestionText.textContent?.trim() || '';
    }

    if (clickedSuggestion && !this.recentSearches.includes(clickedSuggestion)) {
      this.recentSearches.unshift(clickedSuggestion);
      this.recentSearches = this.recentSearches.slice(0, 3);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.recentSearches));
    }

    this.publishSearchSubmit(clickedSuggestion || searchQuery, 'autocomplete_suggestion');
  }

  observeSearchForm() {
    const observer = new MutationObserver(() => {
      const searchForm = document.querySelector(".aa-Form");
      if (searchForm) {
        searchForm.addEventListener(
          "submit",
          this.handleSearchFormSubmit.bind(this)
        );
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  addSearchInputListener() {
    const observer = new MutationObserver(() => {
      const searchInput = document.querySelector(".aa-Input");
      
      if (searchInput) {
        if (!searchInput.dataset.typingListenerAdded) {
          searchInput.dataset.typingListenerAdded = 'true';
          searchInput.addEventListener('input', (e) => {
            
            const value = (e.target.value || '').toString();
            if (value.trim().length === 0) return;
            try {
              this.handleSearchNotNullOrEmpty();
            } catch (e) {
            }
            
            this.publishSearchSubmit(value, 'typing');
          });
        }

        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  handleSearchNotNullOrEmpty() {
    const popularItemsContainer = document.getElementById("opSearchPopularItemsContainer");
    popularItemsContainer.classList.add("tw-hidden");

    const panelLayouts = document.querySelectorAll(".aa-PanelLayout");
    panelLayouts.forEach(panel => panel.classList.remove("tw-hidden"));
  }



}

customElements.define("op-algolia-header-search", OpAlgoliaHeaderSearch);

if (!customElements.get('op-algolia-trending')) {
  class OpAlgoliaTrending extends OpAlgoliaUtilities {

    constructor(){
      super();
      this.fetchBehavior = "trending-items";
      this.fetchStarted = false;
      this.visibilityObserver = null;
    }

    connectedCallback() {
      this.productsFetched = false;
      this.fallbackHandles = this.dataset.fallbackHandles ? this.dataset.fallbackHandles.split(",") : [];
      this.observeVisibility();
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
    }

    observeVisibility() {
      if (this.productsFetched || this.fetchStarted) return;

      if (typeof window.IntersectionObserver !== "function") {
        this.fetchStarted = true;
        this.fetchProducts();
        return;
      }

      this.visibilityObserver?.disconnect();
      this.visibilityObserver = new IntersectionObserver((entries, observer) => {
        if (!entries.some(entry => entry.isIntersecting)) return;

        observer.disconnect();
        this.fetchStarted = true;
        this.fetchProducts();
      }, {
        rootMargin: "0px 0px 150px 0px",
        threshold: 0.1
      });

      this.visibilityObserver.observe(this);
    }

    async fetchProducts() {
      if (this.productsFetched) return;
      try {
        const data = await this.fetchItems(this.fetchBehavior);
        if(!data || !data.results[0].hits) return;

        for(const [index, hit] of data.results[0].hits.entries()){
          //const variantTag = hit._tags.find(tag => tag.startsWith('variant:'));
          //const variantId = variantTag ? variantTag.split(':')[1] : null;
          const productHandle = hit.handle;
          const countEl = index + 1;
          const productAdded = await this.addProductCard(productHandle, countEl);
          //const searchPanelContentValue = productAdded.getAttribute('data-element-id');

          const observer = new MutationObserver(() => {
          const imgWrapper = productAdded.querySelector('[data-pd-image-wrapper]');

          if (imgWrapper) {
              imgWrapper.setAttribute('data-element-id', 'SearchPanel_Content_' + hit.title + '_' + index);
              imgWrapper.setAttribute('data-description', this.fetchBehavior);
              observer.disconnect();
            }
          });

          observer.observe(productAdded, { childList: true, subtree: true });
        }
        this.productsFetched = true;
      } catch(error) {
        console.error('Error fetching items:', error);
        if (this.fallbackHandles.length === 0) return;
        
        this.fallbackHandles.forEach((handle) => {
          if (!handle) return;
          const productCard = document.createElement('op-dynamic-product-card');
          productCard.setAttribute('data-product-template', 'op-product-card');
          productCard.setAttribute('data-product-handle', handle);
          productCard.setAttribute('data-show-on-load', 'true');
          productCard.style.display = "none";
          this.append(productCard);
        });
        this.productsFetched = true;
      }
    }

    async addProductCard(handle, index) {
      const productCardFetchUrl = `${Shopify.routes.root}products/${handle}?view=op-product-card`;
      const productCardId = '#op-collection-product-card';

      try {
        const response = await fetch(productCardFetchUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const cardElement = doc.querySelector(productCardId);
        if (!cardElement) {
          throw new Error(`Product card with ID: ${productCardId} not found in ${productCardFetchUrl}`);
        }
        if (typeof handle.trackSearchAttribution === "function") cardElement.addEventListener("click", () => { handle.trackSearchAttribution() });
        
        const elemToAppend = cardElement.children[0];
        const title = elemToAppend.querySelector('[data-title]')?.dataset.title ?? '';
        const titleCamelCase = OpAlgoliaHeaderSearch.validateDateAnalytics(title);

        elemToAppend.dataset.elementId = `SearchPanel_Content_${titleCamelCase}_${index}`;

        elemToAppend.dataset.description = 'Popular_Search';
        
        this.append(elemToAppend);
        return elemToAppend;
      } catch (error) {
        console.error('Error fetching product card:', error);
      } 
    }
  }
  customElements.define("op-algolia-trending", OpAlgoliaTrending);
}

if (!customElements.get('op-recent-searches-popular')) {
  class OpRecentSearchesPopular extends OpAlgoliaUtilities {
    constructor() {
      super();
      this.localeinfo = JSON.parse(this.dataset.localization);
      this.templateFallback = this.querySelector('.op-recent-searches__popular').content.cloneNode(true);
      this.fetchBehavior = "popular-items";
      this.contentMounted = false;
      this.fetchStarted = false;
      this.visibilityObserver = null;
      this.suggestionsObserver = null;
    }

    connectedCallback() {
      this.observeVisibility();
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect();
      this.suggestionsObserver?.disconnect();
    }

    observeVisibility() {
      if (this.contentMounted || this.fetchStarted) return;

      if (typeof window.IntersectionObserver !== "function") {
        this.fetchStarted = true;
        this.mountContent();
        return;
      }

      this.visibilityObserver?.disconnect();
      this.visibilityObserver = new IntersectionObserver((entries, observer) => {
        if (!entries.some(entry => entry.isIntersecting)) return;

        observer.disconnect();
        this.fetchStarted = true;
        this.mountContent();
      }, {
        rootMargin: "0px 0px 150px 0px",
        threshold: 0.1
      });

      this.visibilityObserver.observe(this);
    }

    mountContent() {
      if (this.contentMounted) return;

      this.fetchItems(this.fetchBehavior)
        .then((data) => {
          this.createPopularSearchesItem(data);
          this.contentMounted = true;
          this.appendToAlgoliaSuggestions();
        })
        .catch((error) => {
          console.error('Error mounting content:', error);
          this.append(this.templateFallback);
          this.contentMounted = true;
          this.appendToAlgoliaSuggestions();
        });
    }

    appendToAlgoliaSuggestions() {
      const insertContent = () => {
        const container = document.querySelector('.algolia-search-suggestions');
        if (!container) return false;

        if (container.querySelector('.op-recent-searches-popular-wrapper')) return true;

        const wrapper = document.createElement('div');
        wrapper.classList.add('op-recent-searches-popular-wrapper');

        this.childNodes.forEach(node => {
          wrapper.append(node.cloneNode(true));
        });

        container.appendChild(wrapper);
        return true;
      };

      insertContent();

      this.suggestionsObserver?.disconnect();
      this.suggestionsObserver = new MutationObserver(() => {
        insertContent();
      });

      this.suggestionsObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    createPopularSearchesItem(data) {
      const { country, language } = this.localeinfo;
      const localeKey = `${language}-${country.toLowerCase()}`;
      data.results[0].hits.forEach((item) => {

        const hrefPopularResult = `/${localeKey}/collections/${item.handle}`;
        
        const popularResultItem = document.createElement("li");
        popularResultItem.className = "tw-pb-md tw-list-none";
        popularResultItem.innerHTML = `
          <a href="${hrefPopularResult}" class="tw-typo-op-base tw-text-op-tertiary" data-element-id="SearchPanel_Content_link" data-description="Most_Searched">
            ${item.title}
          </a>
        `;
        this.append(popularResultItem);
      });
    }
  }

  customElements.define("op-recent-searches-popular", OpRecentSearchesPopular);
}
